import { createContext, PropsWithChildren, useEffect, useMemo } from 'react'
import {
  createIntl,
  createIntlCache,
  defineMessages,
  injectIntl,
  IntlProvider,
  WrappedComponentProps,
} from 'react-intl'
import { isNil } from 'lodash-es'
import { LocaleObject, printValue, setLocale, ValidationError } from 'yup'

export type YupErrorMapProviderProps = PropsWithChildren<Partial<WrappedComponentProps>>

const YupErrorMapContext = createContext<YupErrorMapProviderProps>({})

const intlCache = createIntlCache()

const messages = defineMessages({
  mixedDefault: {
    id: 'yup_errors.mixed.default',
    defaultMessage: '{path} is invalid',
  },
  mixedRequired: {
    id: 'yup_errors.mixed.required',
    defaultMessage: '{path} is a required field',
  },
  mixedDefined: {
    id: 'yup_errors.mixed.defined',
    defaultMessage: '{path} must be defined',
  },
  mixedNotNull: {
    id: 'yup_errors.mixed.not_null',
    defaultMessage: '{path} cannot be null',
  },
  mixedOneOf: {
    id: 'yup_errors.mixed.one_of',
    defaultMessage: '{path} must be one of the following values: {values}',
  },
  mixedNotOneOf: {
    id: 'yup_errors.mixed.not_one_of',
    defaultMessage: '{path} must not be one of the following values: {values}',
  },
  mixedNotTypeMixed: {
    id: 'yup_errors.mixed.not_type_mixed',
    defaultMessage: '{path} must match the configured type; the validated value was: {value}',
  },
  mixedNotTypeNotMixed: {
    id: 'yup_errors.mixed.not_type_not_mixed',
    defaultMessage: "{path} must be a ''{type}'' type, but the final value was: ''{value}''",
  },
  mixedNotTypeCastFrom: {
    id: 'yup_errors.mixed.not_type_cast_from',
    defaultMessage: "(cast from the value ''{value}'')",
  },
  stringLength: {
    id: 'yup_errors.string.length',
    defaultMessage: '{path} must be exactly {length} characters',
  },
  stringMin: {
    id: 'yup_errors.string.min',
    defaultMessage: '{path} must be at least {min} characters',
  },
  stringMax: {
    id: 'yup_errors.string.max',
    defaultMessage: '{path} must be at most {max} characters',
  },
  stringMatches: {
    id: 'yup_errors.string.matches',
    defaultMessage: '{path} must match the following: {regex}',
  },
  stringEmail: {
    id: 'yup_errors.string.email',
    defaultMessage: '{path} must be a valid email',
  },
  stringUrl: {
    id: 'yup_errors.string.url',
    defaultMessage: '{path} must be a valid URL',
  },
  stringUuid: {
    id: 'yup_errors.string.uuid',
    defaultMessage: '{path} must be a valid UUID',
  },
  stringDatetime: {
    id: 'yup_errors.string.datetime',
    defaultMessage: '{path} must be a valid ISO date-time',
  },
  stringDatetimePrecision: {
    id: 'yup_errors.string.datetime_precision',
    defaultMessage: '{path} must be a valid ISO date-time with precision of exactly {precision} digits',
  },
  stringDatetimeOffset: {
    id: 'yup_errors.string.datetime_offset',
    defaultMessage: "{path} must be a valid ISO date-time with UTC 'Z' offset",
  },
  stringTrim: {
    id: 'yup_errors.string.trim',
    defaultMessage: '{path} must be a trimmed string',
  },
  stringLowercase: {
    id: 'yup_errors.string.lowercase',
    defaultMessage: '{path} must be a lowercase string',
  },
  stringUppercase: {
    id: 'yup_errors.string.uppercase',
    defaultMessage: '{path} must be an uppercase string',
  },
  numberMin: {
    id: 'yup_errors.number.min',
    defaultMessage: '{path} must be greater than or equal to {min}',
  },
  numberMax: {
    id: 'yup_errors.number.max',
    defaultMessage: '{path} must be less than or equal to {max}',
  },
  numberLessThan: {
    id: 'yup_errors.number.less_than',
    defaultMessage: '{path} must be less than {less}',
  },
  numberMoreThan: {
    id: 'yup_errors.number.more_than',
    defaultMessage: '{path} must be more than {more}',
  },
  numberPositive: {
    id: 'yup_errors.number.positive',
    defaultMessage: '{path} must be a positive number',
  },
  numberNegative: {
    id: 'yup_errors.number.negative',
    defaultMessage: '{path} must be a negative number',
  },
  numberInteger: {
    id: 'yup_errors.number.integer',
    defaultMessage: '{path} must be an integer',
  },
  dateMin: {
    id: 'yup_errors.date.min',
    defaultMessage: '{path} must be later than {min}',
  },
  dateMax: {
    id: 'yup_errors.date.max',
    defaultMessage: '{path} must be at earlier than {max}',
  },
  booleanIsValue: {
    id: 'yup_errors.boolean.is_value',
    defaultMessage: '{path} must be {value}',
  },
  objectNoUnknown: {
    id: 'yup_errors.object.no_unknown',
    defaultMessage: '{path} must not have unknown keys: {unknown}',
  },
  arrayMin: {
    id: 'yup_errors.array.min',
    defaultMessage: '{path} must have at least {min} items',
  },
  arrayMax: {
    id: 'yup_errors.array.max',
    defaultMessage: '{path} must have at most {max} items',
  },
  arrayLength: {
    id: 'yup_errors.array.length',
    defaultMessage: '{path} must have exactly {length} items',
  },
  tupleNotTypeMin: {
    id: 'yup_errors.tuple.not_type_min',
    defaultMessage: '{path} must have at least {min} items, but has only {length} for value: {value}',
  },
  tupleNotTypeMax: {
    id: 'yup_errors.tuple.not_type_max',
    defaultMessage: '{path} must have at most {max} items, but has {length} items for value: {value}',
  },
})

/**
 * Raw provider that sets the Yup locale via FormatJS.
 *
 * Returns a nested `IntlProvider` with the messages from the
 * parent `IntlProvider`.
 *
 * @see {@link https://github.com/formatjs/formatjs/issues/1109}
 */
const YupErrorMapProviderRaw = ({ intl, children }: YupErrorMapProviderProps) => {
  const parentMessages: Record<string, string> = useMemo(
    () => (intl?.messages as Record<string, string>) ?? {},
    [intl?.messages]
  )

  const { formatMessage } = useMemo(
    () => createIntl({ locale: intl?.locale ?? 'en', messages: parentMessages }, intlCache),
    [intl?.locale, parentMessages]
  )

  useEffect(() => {
    const mixed: Required<LocaleObject['mixed']> = {
      default: ({ path }) => formatMessage(messages['mixedDefault'], { path }),
      required: ({ path }) => formatMessage(messages['mixedRequired'], { path }),
      defined: ({ path }) => formatMessage(messages['mixedDefined'], { path }),
      notNull: ({ path, values }) => formatMessage(messages['mixedNotNull'], { path, values }),
      oneOf: ({ path, values }) => formatMessage(messages['mixedOneOf'], { path, values }),
      notOneOf: ({ path, values }) => formatMessage(messages['mixedNotOneOf'], { path, values }),
      notType: ({ path, type, value, originalValue }) => {
        const castMessage =
          !isNil(originalValue) && originalValue !== value
            ? ` ${formatMessage(messages['mixedNotTypeCastFrom'], { value: printValue(originalValue, true) })}`
            : ''

        return type !== 'mixed'
          ? formatMessage(messages['mixedNotTypeNotMixed'], { path, type, value: printValue(value, true) }) +
              castMessage
          : formatMessage(messages['mixedNotTypeMixed'], { path, value: printValue(value, true) }) + castMessage
      },
    }

    const string: Required<LocaleObject['string']> = {
      length: ({ path, length }) => formatMessage(messages['stringLength'], { path, length }),
      min: ({ path, min }) => formatMessage(messages['stringMin'], { path, min }),
      max: ({ path, max }) => formatMessage(messages['stringMax'], { path, max }),
      matches: ({ path, regex }) => formatMessage(messages['stringMatches'], { path, regex: String(regex) }),
      email: ({ path }) => formatMessage(messages['stringEmail'], { path }),
      url: ({ path }) => formatMessage(messages['stringUrl'], { path }),
      uuid: ({ path }) => formatMessage(messages['stringUuid'], { path }),
      datetime: ({ path }) => formatMessage(messages['stringDatetime'], { path }),
      datetime_precision: ({ path, precision }) =>
        formatMessage(messages['stringDatetimePrecision'], { path, precision }),
      datetime_offset: ({ path }) => formatMessage(messages['stringDatetimeOffset'], { path }),
      trim: ({ path }) => formatMessage(messages['stringTrim'], { path }),
      lowercase: ({ path }) => formatMessage(messages['stringLowercase'], { path }),
      uppercase: ({ path }) => formatMessage(messages['stringUppercase'], { path }),
    }

    const number: Required<LocaleObject['number']> = {
      min: ({ path, min }) => formatMessage(messages['numberMin'], { path, min }),
      max: ({ path, max }) => formatMessage(messages['numberMax'], { path, max }),
      lessThan: ({ path, less }) => formatMessage(messages['numberLessThan'], { path, less }),
      moreThan: ({ path, more }) => formatMessage(messages['numberMoreThan'], { path, more }),
      positive: ({ path }) => formatMessage(messages['numberPositive'], { path }),
      negative: ({ path }) => formatMessage(messages['numberNegative'], { path }),
      integer: ({ path }) => formatMessage(messages['numberInteger'], { path }),
    }

    const date: Required<LocaleObject['date']> = {
      min: ({ path, min }) => formatMessage(messages['dateMin'], { path, min }),
      max: ({ path, max }) => formatMessage(messages['dateMax'], { path, max }),
    }

    const boolean: Required<LocaleObject['boolean']> = {
      isValue: ({ path }) => formatMessage(messages['booleanIsValue'], { path }),
    }

    const object: Required<LocaleObject['object']> = {
      noUnknown: ({ path }) => formatMessage(messages['objectNoUnknown'], { path }),
    }

    const array: Required<LocaleObject['array']> = {
      min: ({ path, min }) => formatMessage(messages['arrayMin'], { path, min }),
      max: ({ path, max }) => formatMessage(messages['arrayMax'], { path, max }),
      length: ({ path, length }) => formatMessage(messages['arrayLength'], { path, length }),
    }

    const tuple: Required<LocaleObject['tuple']> = {
      notType: (params) => {
        const { path, value, spec } = params
        const typeLength = spec.types.length

        if (Array.isArray(value)) {
          if (value.length < typeLength)
            return formatMessage(messages['tupleNotTypeMin'], {
              path,
              min: typeLength,
              length: value.length,
              value: printValue(value, true),
            })
          if (value.length > typeLength)
            return formatMessage(messages['tupleNotTypeMax'], {
              path,
              max: typeLength,
              length: value.length,
              value: printValue(value, true),
            })
        }

        return ValidationError.formatError(mixed.notType, params)
      },
    }

    const locale: Required<LocaleObject> = {
      mixed,
      string,
      number,
      date,
      object,
      array,
      boolean,
      tuple,
    }

    setLocale(locale)
  }, [formatMessage])

  return (
    <IntlProvider locale={intl?.locale ?? 'en'} messages={parentMessages}>
      <YupErrorMapContext.Provider value={{ intl }}>{children}</YupErrorMapContext.Provider>
    </IntlProvider>
  )
}

/**
 * Provider that sets the Yup locale via FormatJS.
 *
 * !!!: Relies on `IntlProvider` from `react-intl` being present in the component tree.
 */
export const YupErrorMapProvider = injectIntl(YupErrorMapProviderRaw)
